import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import CarrierTrackingLink from '../CarrierTrackingLink';
import Divider from '../../../ui/Divider/Divider';
import { useSettings } from '../../../../hooks/useSettings';
import { useTranslations } from '../../../../hooks/useTranslations';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarClickTypes,
  NarvarClickSubTypes,
  NarvarEventComponents,
} from '../../../../hooks/Analytics';
import { TrackingInfoEvent } from '../../../../../../__generated__/loma';
import useOrderTracking from '../../../../hooks/useOrderTracking';
import { useTheme } from '../../../../hooks/useTheme';
import styles from './ShippingActivity.module.css';
import { useHackStyle } from './hooks/useHackStyle';
import { FeatureLayout } from '../../DynamicLayout/layoutTree';
import ShippingHistoryToggle from './components/ShippingHistoryToggle';

export interface TrackingEventsProps {
  featureName?: string;
  layout?: FeatureLayout;
}

export interface ShippingActivityProps {
  featureName?: string;
  layout?: FeatureLayout;
}

const TrackingEvents = ({
  featureName,
  layout = FeatureLayout.PORTRAIT,
}: TrackingEventsProps) => {
  const { bodytextDefaultNone, smalltextDefaultNone, smalltextEmphasisNone } =
    useTheme();

  const { cssClass } = useHackStyle();

  const { trackingStatusNoEddSubtext } = useTranslations();

  const { getCurrentShipment } = useOrderTracking();
  const currentShipment = getCurrentShipment();
  const { eddPanel } = useSettings();

  const { expandEventHistoryByDefault = false } = eddPanel || {};

  const [collapsed, setCollapsed] = useState(!expandEventHistoryByDefault);

  useEffect(() => {
    setCollapsed(!expandEventHistoryByDefault);
  }, [expandEventHistoryByDefault]);

  const logEvent = useAnalyticsPost();

  if (!currentShipment) return null;

  const { history: fullHistory, trackingNumber, carrier } = currentShipment;

  if (!fullHistory || fullHistory.length === 0)
    return (
      <>
        <p
          style={{
            lineHeight: '1.25rem',
            marginTop: 0,
            ...bodytextDefaultNone,
          }}
        >
          {trackingStatusNoEddSubtext}
        </p>
        {trackingNumber && (
          <>
            <Divider />
            <CarrierTrackingLink featureName={featureName} layout={layout} />
          </>
        )}
      </>
    );

  const history = collapsed ? [fullHistory[0]] : fullHistory;

  const historyGroupedByDate = history.reduce(
    (
      acc: Map<string, TrackingInfoEvent[]>,
      event: TrackingInfoEvent,
    ): Map<string, TrackingInfoEvent[]> => {
      const {
        dateFormatted: [date],
      } = event;
      const eventList = acc.get(date);
      if (eventList) {
        eventList.push(event);
      } else {
        acc.set(date, [event]);
      }
      return acc;
    },
    new Map<string, TrackingInfoEvent[]>(),
  );

  const handleToggleHistoryClick = () => {
    const clickDescription = collapsed
      ? NarvarClickSubTypes.SHOW_SHIPPING_HISTORY
      : NarvarClickSubTypes.HIDE_SHIPPING_HISTORY;

    logEvent({
      clickCategory: clickDescription,
      clickSubType: clickDescription,
      clickType: NarvarClickTypes.INTERACTION,
      eventCarrierMoniker: carrier,
      eventComponent: NarvarEventComponents.NTH_BUNDLE_SHIPPING_ACTIVITY,
      eventFeature: NarvarEventComponents.NTH_BUNDLE_SHIPPING_ACTIVITY,
      eventTrackingNumber: trackingNumber,
      eventType: NarvarEventTypes.CLICK,
      feature: featureName,
      pageComponent: NarvarEventComponents.NTH_BUNDLE_SHIPPING_ACTIVITY,
    });

    setCollapsed(!collapsed);
  };

  return (
    <>
      {layout === FeatureLayout.PORTRAIT && <Divider />}
      <div
        className={clsx({
          [styles.shippingActivity]: layout === FeatureLayout.PORTRAIT,
          [styles.shippingActivityLandscape]:
            layout === FeatureLayout.LANDSCAPE,
        })}
      >
        <>
          <ShippingHistoryToggle
            collapsed={collapsed}
            onClick={handleToggleHistoryClick}
          />
          <ol
            className={styles.orderedList}
            data-test="shippingHistoryEventsContainer"
          >
            {[...historyGroupedByDate.entries()].map(
              ([date, eventList], idx) => (
                <li
                  className={styles.orderedlistItem}
                  key={`${date}-${idx}`}
                  style={{ marginTop: idx && '1.5rem' }}
                >
                  <span className={styles.date} style={smalltextEmphasisNone}>
                    <time>{date}</time>
                  </span>
                  <ol className={styles.orderedList}>
                    {eventList.map(
                      (
                        {
                          description,
                          formattedDescription,
                          city,
                          state,
                          dateFormatted: [, time],
                        },
                        index,
                      ) => {
                        const timeAndLocation = (
                          <>
                            <time>{time}</time>
                            {time && (city || state) && (
                              <span aria-hidden="true">&nbsp;|&nbsp;</span>
                            )}
                            <span>
                              {city}
                              {city && state && ', '}
                              {state}
                            </span>
                          </>
                        );

                        const descriptionElement =
                          formattedDescription &&
                          formattedDescription.format === 'HTML' &&
                          formattedDescription.value ? (
                            <p
                              className={cssClass}
                              style={{ ...bodytextDefaultNone, marginTop: '0' }}
                              dangerouslySetInnerHTML={{
                                __html: formattedDescription.value,
                              }}
                            />
                          ) : (
                            <p style={{ ...bodytextDefaultNone, margin: 0 }}>
                              {description}
                            </p>
                          );

                        return (
                          <li
                            key={`${date}${time}${city ?? ''}${state ?? ''}${
                              description ?? ''
                            }-${index}`}
                            className={styles.orderedlistItem}
                          >
                            <span style={smalltextDefaultNone}>
                              {timeAndLocation}
                            </span>
                            {descriptionElement}
                          </li>
                        );
                      },
                    )}
                  </ol>
                </li>
              ),
            )}
          </ol>
          {trackingNumber && !collapsed && (
            <>
              <Divider />
              <CarrierTrackingLink featureName={featureName} layout={layout} />
            </>
          )}
        </>
      </div>
    </>
  );
};

export default TrackingEvents;
